// Zoom-style bottom toolbar: icon over label, device menus on the mic and video carets, Leave / End on the right.
// `where` 'popout' (its own window: no screen sharing from there; a button brings it back); `compact` = corner tile.
import { useState } from 'react';
import {
  Mic, MicOff, Video, VideoOff, MonitorUp, MonitorX, Pencil, Eraser, PhoneOff, MessageSquare, Users, Hand, ChevronUp,
  AppWindow, Volume2,
} from 'lucide-react';
import { MenuWrap, MenuLabel, MenuItem, MenuDivider } from './Menu';
import { playTestSound, speakerSupported } from './media';
import BackgroundPicker from './BackgroundPicker';

function Btn({ tid, label, title, onClick, active, disabled, danger, warn, narrow, children, badge }) {
  return (
    <button type="button" data-testid={tid} onClick={onClick} disabled={disabled} title={title || label} aria-label={title || label}
      className={`group relative flex h-14 flex-col items-center justify-center gap-1 rounded-lg px-2 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4575A8] disabled:cursor-not-allowed disabled:opacity-35 ${narrow ? 'min-w-[44px]' : 'min-w-[64px]'} ${active ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}>
      <span className={`transition-transform duration-150 group-hover:scale-105 ${danger ? 'text-red-500' : warn ? 'text-amber-400' : ''}`}>{children}</span>
      {!narrow && <span className="whitespace-nowrap text-[11px] font-medium tracking-wide">{label}</span>}
      {badge}
    </button>
  );
}

const Badge = ({ n, tid, tone = 'bg-red-500' }) => (n > 0 ? (
  <span className={`absolute right-1.5 top-1 min-w-[18px] rounded-full ${tone} px-1 text-[10px] font-bold leading-[18px] text-white`} data-testid={tid}>{n > 9 ? '9+' : n}</span>
) : null);

function Caret({ open, setOpen, tid, label }) {
  return (
    <button type="button" onClick={() => setOpen((o) => !o)} aria-label={label} title={label} data-testid={tid}
      className={`-ml-0.5 mr-1 flex h-14 w-6 items-center justify-center rounded-md transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4575A8] ${open ? 'bg-white/15 text-white' : 'text-slate-200 hover:bg-white/10 hover:text-white'}`}>
      <ChevronUp className="h-4 w-4" strokeWidth={2.5} />
    </button>
  );
}

function AudioMenu({ dc, narrow }) {
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const { mics, speakers, micId, speakerId } = dc.devices;
  const on = !!dc.local?.audio;
  const test = async () => { setTesting(true); try { await playTestSound(speakerId); } catch { /* blocked */ } setTesting(false); };
  return (
    <MenuWrap open={open} setOpen={setOpen} tid="mm-audio-menu" anchor={(
      <>
        <Btn tid="mm-ctl-mic" label={on ? 'Mute' : 'Unmute'} onClick={() => dc.setMic(!on)} danger={!on} narrow={narrow}>
          {on ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </Btn>
        <Caret open={open} setOpen={setOpen} tid="mm-ctl-mic-menu" label="Microphone and speaker" />
      </>
    )}>
      <MenuLabel>Microphone</MenuLabel>
      {mics.length ? mics.map((d, i) => (
        <MenuItem key={d.deviceId} checked={d.deviceId === micId} onClick={() => { dc.setDevice('mic', d.deviceId); setOpen(false); }} tid={`mm-mic-opt-${i}`}>
          {d.label || `Microphone ${i + 1}`}
        </MenuItem>
      )) : <p className="px-2.5 py-1.5 text-xs text-slate-400">No microphone found</p>}
      <MenuDivider />
      <MenuLabel>Speaker</MenuLabel>
      {speakerSupported() && speakers.length ? speakers.map((d, i) => (
        <MenuItem key={d.deviceId} checked={d.deviceId === speakerId} onClick={() => dc.setSpeaker(d.deviceId)} tid={`mm-speaker-opt-${i}`}>
          {d.label || `Speaker ${i + 1}`}
        </MenuItem>
      )) : <p className="px-2.5 py-1.5 text-xs text-slate-400">This browser plays sound through your computer's default speaker.</p>}
      <MenuItem onClick={test} disabled={testing} tid="mm-speaker-test"><span className="inline-flex items-center gap-1.5"><Volume2 className="h-3.5 w-3.5" />{testing ? 'Playing…' : 'Play test sound'}</span></MenuItem>
    </MenuWrap>
  );
}

function VideoMenu({ dc, narrow, guest }) {
  const [open, setOpen] = useState(false);
  const { cameras, camId } = dc.devices;
  const on = !!dc.local?.video;
  return (
    <MenuWrap open={open} setOpen={setOpen} tid="mm-video-menu" wide anchor={(
      <>
        <Btn tid="mm-ctl-cam" label={on ? 'Stop Video' : 'Start Video'} onClick={() => dc.setCam(!on)} danger={!on} narrow={narrow}>
          {on ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </Btn>
        <Caret open={open} setOpen={setOpen} tid="mm-ctl-cam-menu" label="Camera and background" />
      </>
    )}>
      <MenuLabel>Camera</MenuLabel>
      {cameras.length ? cameras.map((d, i) => (
        <MenuItem key={d.deviceId} checked={d.deviceId === camId} onClick={() => { dc.setDevice('camera', d.deviceId); setOpen(false); }} tid={`mm-cam-opt-${i}`}>
          {d.label || `Camera ${i + 1}`}
        </MenuItem>
      )) : <p className="px-2.5 py-1.5 text-xs text-slate-400">No camera found</p>}
      <MenuDivider />
      <MenuLabel>Background</MenuLabel>
      <div className="px-2.5 pb-2 pt-1"><BackgroundPicker dc={dc} guest={guest} dark tid="mm-ctl-bg" /></div>
    </MenuWrap>
  );
}

function LeaveBtn({ dc, host, narrow }) {
  const [open, setOpen] = useState(false);
  const pill = 'flex h-10 items-center gap-1.5 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white transition-colors duration-150 hover:bg-red-700';
  if (!host?.canEndAll) {
    return <button type="button" className={pill} onClick={dc.leave} data-testid="mm-ctl-leave" title="Leave"><PhoneOff className="h-4 w-4" />{!narrow && 'Leave'}</button>;
  }
  return (
    <MenuWrap open={open} setOpen={setOpen} align="right" tid="mm-end-menu" anchor={(
      <button type="button" className={pill} onClick={() => setOpen((o) => !o)} data-testid="mm-ctl-leave" title="End or leave">
        <PhoneOff className="h-4 w-4" />{!narrow && 'End'}
      </button>
    )}>
      <MenuItem onClick={() => { setOpen(false); host.askLeave(); }} tid="active-call-leave">Leave meeting</MenuItem>
      <MenuItem danger onClick={() => { setOpen(false); host.askEnd(); }} tid="active-call-end-all">End meeting for all</MenuItem>
    </MenuWrap>
  );
}

export default function Toolbar({ dc, where = 'app', compact, narrow, guest, sharing, annotate, setAnnotate, onClear, panel, setPanel, unread, people, host, pop, onExpand, onLeave }) {
  const joined = dc.phase === 'joined';
  const hand = Number(dc.local?.userData?.hand) > 0;
  const togglePanel = (p) => (compact ? () => { setPanel(p); onExpand?.(); } : () => setPanel((cur) => (cur === p ? null : p)));
  if (compact) {
    const sm = 'relative flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-150';
    return (
      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center gap-1.5 bg-[#0D1B2A]/90 p-2" data-testid="mm-controls">
        <button type="button" className={`${sm} ${dc.local?.audio ? 'bg-white/15 text-white' : 'bg-red-600 text-white'}`} onClick={() => dc.setMic(!dc.local?.audio)} data-testid="mm-ctl-mic" aria-label="Mute or unmute">
          {dc.local?.audio ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
        </button>
        <button type="button" className={`${sm} ${dc.local?.video ? 'bg-white/15 text-white' : 'bg-red-600 text-white'}`} onClick={() => dc.setCam(!dc.local?.video)} data-testid="mm-ctl-cam" aria-label="Start or stop video">
          {dc.local?.video ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
        </button>
        <button type="button" className={`${sm} bg-white/15 text-white`} onClick={togglePanel('chat')} disabled={!joined} data-testid="mm-ctl-chat" aria-label="Chat">
          <MessageSquare className="h-4 w-4" /><Badge n={unread} tid="mm-chat-unread" />
        </button>
        <button type="button" className={`${sm} ${hand ? 'bg-amber-400 text-black' : 'bg-white/15 text-white'}`} onClick={() => dc.setHand(!hand)} disabled={!joined} data-testid="mm-ctl-hand" aria-label={hand ? 'Lower hand' : 'Raise hand'}>
          <Hand className="h-4 w-4" />
        </button>
        <button type="button" className={`${sm} bg-red-600 text-white hover:bg-red-700`} onClick={onLeave || dc.leave} data-testid="mm-ctl-leave" aria-label="Leave"><PhoneOff className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-t border-white/10 bg-[#0D1B2A] px-3 py-1.5" data-testid="mm-controls">
      <div className="flex items-center">
        <AudioMenu dc={dc} narrow={narrow} />
        <VideoMenu dc={dc} narrow={narrow} guest={guest} />
      </div>
      <div className="flex items-center gap-0.5">
        <Btn tid="mm-ctl-people" label="Participants" onClick={togglePanel('participants')} active={panel === 'participants'} narrow={narrow}
          badge={<Badge n={people} tid="mm-people-count" tone="bg-white/20" />}>
          <Users className="h-5 w-5" />
        </Btn>
        <Btn tid="mm-ctl-chat" label="Chat" onClick={togglePanel('chat')} active={panel === 'chat'} disabled={!joined} narrow={narrow}
          badge={<Badge n={unread} tid="mm-chat-unread" />}>
          <MessageSquare className="h-5 w-5" />
        </Btn>
        {where === 'app' && (
          <Btn tid="mm-ctl-share" label={dc.local?.screen ? 'Stop Share' : 'Share Screen'} onClick={dc.toggleShare} disabled={!joined} narrow={narrow}>
            {dc.local?.screen ? <MonitorX className="h-5 w-5 text-red-400" /> : <MonitorUp className="h-5 w-5 text-[#22C55E]" />}
          </Btn>
        )}
        <Btn tid="mm-ctl-hand" label={hand ? 'Lower Hand' : 'Raise Hand'} onClick={() => dc.setHand(!hand)} active={hand} warn={hand} disabled={!joined} narrow={narrow}>
          <Hand className="h-5 w-5" />
        </Btn>
        {sharing && (
          <>
            <Btn tid="mm-ctl-annotate" label="Annotate" onClick={() => setAnnotate((a) => !a)} active={annotate} warn={annotate} narrow={narrow}><Pencil className="h-5 w-5" /></Btn>
            <Btn tid="mm-ctl-clear" label="Clear" onClick={onClear} narrow={narrow}><Eraser className="h-5 w-5" /></Btn>
          </>
        )}
        {where === 'popout' && <Btn tid="mm-ctl-popin" label="Back to app" onClick={pop.close} narrow={narrow}><AppWindow className="h-5 w-5" /></Btn>}
      </div>
      <LeaveBtn dc={dc} host={host} narrow={narrow} />
    </div>
  );
}
